import type { DateValue } from "@internationalized/date";
import { parseDate } from "@internationalized/date";
import { useEffect, useRef } from "react";

interface DateRangeUrlSyncOptions {
  /**
   * Current date range from useExplorerDateFilter
   */
  dateRange: { start: DateValue; end: DateValue } | null;

  /**
   * Function to change date range (from useExplorerDateFilter)
   */
  changeDate: (range: { start: DateValue; end: DateValue } | null) => void;

  /**
   * Navigate function from TanStack Router
   */
  navigate: (options: { search: (prev: any) => any; replace?: boolean }) => void;

  /**
   * Current URL search params
   */
  currentSearch: {
    startDate?: string;
    endDate?: string;
    [key: string]: any;
  };

  /**
   * Whether the date filter is currently applied (from useExplorerDateFilter)
   */
  isApplied: boolean;
}

/**
 * Custom hook to sync date range with URL parameters (truly bidirectional)
 *
 * This enables:
 * - Shareable URLs with specific date ranges
 * - Persistent date selection on page reload
 * - Bookmarkable date-filtered views
 * - Date range persists across page navigation (shared via global Zustand store)
 * - Browser back/forward button support (URL changes update the store)
 * - Link navigation with dates (store updates when URL params change)
 *
 * @example
 * ```tsx
 * const { startDate, endDate } = route.useSearch();
 * const navigate = route.useNavigate();
 * const { dateRange, changeDate, isApplied } = useExplorerDateFilter();
 *
 * useDateRangeUrlSync({
 *   dateRange,
 *   changeDate,
 *   navigate,
 *   currentSearch: { startDate, endDate },
 *   isApplied
 * });
 * ```
 */
export function useDateRangeUrlSync({
  dateRange,
  changeDate,
  navigate,
  currentSearch,
  isApplied,
}: DateRangeUrlSyncOptions) {
  // Track initialization state
  const isInitialized = useRef(false);
  const isInitializing = useRef(false);

  // Track last known values to detect real changes
  const lastUrlDates = useRef<{ startDate?: string; endDate?: string }>({});
  const lastStoreDates = useRef<{ startDate?: string; endDate?: string }>({});

  const urlStartDate = currentSearch.startDate;
  const urlEndDate = currentSearch.endDate;
  const storeStartDate = dateRange?.start.toString();
  const storeEndDate = dateRange?.end.toString();

  // =========================
  // 1. INITIALIZATION (once on mount)
  // =========================
  useEffect(() => {
    // Only run once on mount
    if (isInitialized.current) return;

    isInitializing.current = true;
    isInitialized.current = true;

    // Priority 1: If URL has dates, use them (URL is source of truth on page load)
    if (urlStartDate && urlEndDate) {
      try {
        const start = parseDate(urlStartDate);
        const end = parseDate(urlEndDate);
        changeDate({ start, end });

        // Mark these dates as "synced" to prevent immediate re-sync
        lastUrlDates.current = { startDate: urlStartDate, endDate: urlEndDate };
        lastStoreDates.current = { startDate: urlStartDate, endDate: urlEndDate };
      } catch (error) {
        console.warn("Failed to parse date from URL:", error);
      }
    }
    // Priority 2: If URL has no dates but store has dates (from previous page), sync store to URL
    else if (!urlStartDate && !urlEndDate && isApplied && storeStartDate && storeEndDate) {
      navigate({
        search: (prev) => ({
          ...prev,
          startDate: storeStartDate,
          endDate: storeEndDate,
        }),
        replace: true,
      });

      // Mark these dates as "synced"
      lastUrlDates.current = { startDate: storeStartDate, endDate: storeEndDate };
      lastStoreDates.current = { startDate: storeStartDate, endDate: storeEndDate };
    }
    // Priority 3: No dates anywhere - just mark current state as baseline
    else {
      lastStoreDates.current = { startDate: storeStartDate, endDate: storeEndDate };
      lastUrlDates.current = { startDate: urlStartDate, endDate: urlEndDate };
    }

    // Initialization complete
    setTimeout(() => {
      isInitializing.current = false;
    }, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once

  // =========================
  // 2. URL → STORE SYNC (browser back/forward, link navigation)
  // =========================
  useEffect(() => {
    // Skip if not initialized or currently initializing
    if (!isInitialized.current || isInitializing.current) return;

    // Check if URL actually changed
    const urlChanged = urlStartDate !== lastUrlDates.current.startDate || urlEndDate !== lastUrlDates.current.endDate;

    if (!urlChanged) return;

    // URL changed externally - update store
    lastUrlDates.current = { startDate: urlStartDate, endDate: urlEndDate };

    if (urlStartDate && urlEndDate) {
      // URL has dates - set them in store
      try {
        const start = parseDate(urlStartDate);
        const end = parseDate(urlEndDate);
        changeDate({ start, end });

        // Also update store tracking to prevent loop
        lastStoreDates.current = { startDate: urlStartDate, endDate: urlEndDate };
      } catch (error) {
        console.warn("Failed to parse date from URL:", error);
      }
    } else if (!urlStartDate && !urlEndDate) {
      // URL has no dates - clear store if it has dates
      if (isApplied) {
        changeDate(null);
        lastStoreDates.current = {};
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlStartDate, urlEndDate]); // Only react to URL changes

  // =========================
  // 3. STORE → URL SYNC (user changes date in UI)
  // =========================
  useEffect(() => {
    // Skip if not initialized or currently initializing
    if (!isInitialized.current || isInitializing.current) return;

    // Check if store actually changed
    const storeChanged =
      storeStartDate !== lastStoreDates.current.startDate || storeEndDate !== lastStoreDates.current.endDate;

    if (!storeChanged) return;

    // Store changed - update URL
    lastStoreDates.current = { startDate: storeStartDate, endDate: storeEndDate };

    if (dateRange && isApplied && storeStartDate && storeEndDate) {
      // Store has dates - set them in URL
      navigate({
        search: (prev) => ({
          ...prev,
          startDate: storeStartDate,
          endDate: storeEndDate,
        }),
        replace: true,
      });

      // Also update URL tracking to prevent loop
      lastUrlDates.current = { startDate: storeStartDate, endDate: storeEndDate };
    } else if (!isApplied) {
      // Store has no dates - clear URL
      navigate({
        search: (prev) => {
          const { startDate: _, endDate: __, ...rest } = prev;
          return rest;
        },
        replace: true,
      });

      // Also update URL tracking to prevent loop
      lastUrlDates.current = {};
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeStartDate, storeEndDate, isApplied]); // Only react to store changes
}
